import {FC, useState} from 'react'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle,
    ModalXCloseButton
} from '@/components/ui/modal-atoms/ModalAtoms'
import {Button} from '@/components/ui/button/Button'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {InputText} from '@/components/commons/input-text/InputText'
import {useTranslation} from '@/translations/i18n'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {LARGE_SEND_THRESHOLD} from '@/features/mailer/constants'
import {formatBytes} from '@/features/mailer/utils'

interface MailerSendConfirmationDialogProps {
    onClose: () => void
    onConfirm: () => void
    isSending: boolean
    subject: string
    attachments: File[]
    patientsCount: number
    consultantsCount: number
    includePatients: boolean
    includeConsultants: boolean
    hasPatientFilters: boolean
    hasConsultantFilters: boolean
}

const CONFIRM_WORD = 'INVIA'

export const MailerSendConfirmationDialog: FC<MailerSendConfirmationDialogProps> = ({
    onClose,
    onConfirm,
    isSending,
    subject,
    attachments,
    patientsCount,
    consultantsCount,
    includePatients,
    includeConsultants,
    hasPatientFilters,
    hasConsultantFilters
}) => {
    const {t} = useTranslation()
    const [confirmText, setConfirmText] = useState('')

    const recipientCount = patientsCount + consultantsCount
    const requiresTypeConfirm = recipientCount >= LARGE_SEND_THRESHOLD
    const canConfirm = !requiresTypeConfirm || confirmText.trim().toUpperCase() === CONFIRM_WORD

    const showAllPatientsWarning = includePatients && !hasPatientFilters
    const showAllConsultantsWarning = includeConsultants && !hasConsultantFilters

    const totalAttachmentsSize = attachments.reduce((sum, f) => sum + f.size, 0)

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="520px">
                <ModalHeader>
                    <Flexbox justify="space-between" align="center" fullWidth>
                        <ModalTitle>{t('mailer:send_confirmation_title')}</ModalTitle>
                        <ModalXCloseButton onClick={onClose} />
                    </Flexbox>
                </ModalHeader>
                <ModalBody>
                    <Flexbox direction="column" gap={4} fullWidth>
                        <Flexbox direction="column" gap={1} fullWidth>
                            <Text size="textXs" weight={600}>
                                {t('mailer:send_confirmation_subject_label')}
                            </Text>
                            <Text size="textSm">{subject || '—'}</Text>
                        </Flexbox>

                        <Flexbox direction="column" gap={1} fullWidth>
                            <Text size="textXs" weight={600}>
                                {t('mailer:send_confirmation_recipients_label', {count: recipientCount})}
                            </Text>
                            {includePatients && (
                                <Text size="textSm">
                                    • {t('mailer:send_confirmation_recipients_patients', {count: patientsCount})}
                                </Text>
                            )}
                            {includeConsultants && (
                                <Text size="textSm">
                                    •{' '}
                                    {t('mailer:send_confirmation_recipients_consultants', {
                                        count: consultantsCount
                                    })}
                                </Text>
                            )}
                        </Flexbox>

                        {attachments.length > 0 && (
                            <Flexbox direction="column" gap={1} fullWidth>
                                <Text size="textXs" weight={600}>
                                    {t('mailer:send_confirmation_attachments_label', {
                                        count: attachments.length,
                                        size: formatBytes(totalAttachmentsSize)
                                    })}
                                </Text>
                                <Text size="textSm">{attachments.map(f => f.name).join(', ')}</Text>
                            </Flexbox>
                        )}

                        {showAllPatientsWarning && <InputHelpText error={t('mailer:send_all_patients_warning')} />}
                        {showAllConsultantsWarning && (
                            <InputHelpText error={t('mailer:send_all_consultants_warning')} />
                        )}

                        {import.meta.env.DEV && (
                            <InputHelpText
                                helpText={t('mailer:send_dev_redirect_warning', {email: 'luca.denni@humans.tech'})}
                            />
                        )}

                        {requiresTypeConfirm && (
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Text size="textSm">{t('mailer:send_confirmation_type_to_confirm')}</Text>
                                <InputText
                                    type="text"
                                    placeholder={CONFIRM_WORD}
                                    value={confirmText}
                                    onChange={e => setConfirmText(e.target.value)}
                                    disabled={isSending}
                                />
                            </Flexbox>
                        )}
                    </Flexbox>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" onClick={onClose} type="button" disabled={isSending}>
                        <Text size="textMd">{t('commons:cancel')}</Text>
                    </Button>
                    <Button
                        variant="primaryNeutral"
                        onClick={onConfirm}
                        disabled={isSending || !canConfirm}
                        type="button"
                    >
                        <Text size="textMd">{isSending ? t('mailer:sending') : t('mailer:send')}</Text>
                        {isSending && <Spinner />}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
